import { useMemo, useCallback } from "react";
import {
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  type EntitySelectorConfig,
  type EntitySelectorVariant,
  type EntitySelection,
} from "@salestrak/ui";
import { useIssueSourcePool } from "./hooks";
import { type SelectorSize } from "./sizes";
import { selectionToIssueSourceEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import type { Omittable } from "@salestrak/ui";

export type IssueSourceSelectorProps = Omittable<{
  value?: string | null;
  onValueChange?: (issueSourceId: string) => void;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  size?: SelectorSize;
  disabled?: boolean;
  /** Render a static, non-dimmed value (no picker) — view/comment tier. */
  readOnly?: boolean;
  children?: React.ReactNode;
}>;

export function IssueSourceSelector({
  value,
  onValueChange,
  variant,
  placeholder = "Select source...",
  size,
  disabled,
  readOnly,
  children,
}: IssueSourceSelectorProps) {
  const { pool } = useIssueSourcePool();

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections: 1,
      searchPlaceholder: "Search sources...",
      emptyMessage: "No sources found",
      noItemsMessage: "No lead sources yet",
    }),
    [pool],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    if (!value) return [];
    const entity = pool.items.find((item) => item.id === value);
    if (!entity) return [];
    return [{ id: value, poolId: pool.id, entity }];
  }, [value, pool]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      const selected = next[0];
      if (selected) onValueChange?.(selected.id);
    },
    [onValueChange],
  );

  return (
    <EntitySelector variant={variant} config={config} value={selections} onValueChange={handleValueChange}>
      {children ?? (
        <>
          <SelectionEntityTrigger
            toEntity={selectionToIssueSourceEntity}
            placeholder={placeholder}
            title="Source"
            size={size}
            disabled={disabled}
            readOnly={readOnly}
          />
          <EntitySelectorContent>
            <EntitySelectorInput />
            <EntitySelectorPool value={pool.id} />
            <EntitySelectorEmpty
              noItems={{
                icon: "tag",
                message: "No lead sources yet",
                description: "Add sources in workspace settings.",
              }}
            />
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}
